import { Component } from '@theme/component';

/**
 * A custom element that manages the interactive mega-menu with collections and products.
 *
 * @typedef {Object} Refs
 * @property {HTMLElement} collectionList - The collections list container
 * @property {HTMLElement} productDisplay - The product display container
 *
 * @extends {Component<Refs>}
 */
class InteractiveMegaMenu extends Component {
  requiredRefs = ['collectionList', 'productDisplay'];

  /**
   * Currently active collection handle
   * @type {string | null}
   */
  #activeCollection = null;

  /**
   * Debounce timeout for collection clicks
   * @type {number | null}
   */
  #debounceTimeout = null;

  /**
   * Debounce delay in milliseconds
   * @constant {number}
   */
  #DEBOUNCE_DELAY = 100;

  connectedCallback() {
    super.connectedCallback();

    // Only activate on desktop
    if (window.innerWidth >= 1025) {
      this.#setupCollectionItems();
      this.#activateFirstCollection();
      this.#setupMenuCloseListener();
    }
  }

  /**
   * Setup listener to clean up when menu closes
   */
  #setupMenuCloseListener() {
    const parentListItem = this.closest('.menu-list__list-item');
    if (parentListItem) {
      parentListItem.addEventListener('pointerleave', this.#handleMenuClose.bind(this));
    }
  }

  /**
   * Clean up inline styles when menu closes
   */
  #handleMenuClose() {
    // Remove inline --submenu-height from all elements we set it on
    const headerComponent = /** @type {HTMLElement | null} */ (this.closest('header-component'));
    const headerMenu = /** @type {HTMLElement | null} */ (this.closest('header-menu-component'));
    const overflowMenu = /** @type {HTMLElement | null} */ (document.querySelector('.overflow-menu'));

    if (headerComponent) {
      headerComponent.style.removeProperty('--submenu-height');
    }
    if (headerMenu) {
      headerMenu.style.removeProperty('--submenu-height');
    }
    if (overflowMenu) {
      overflowMenu.style.removeProperty('--submenu-height');
    }
  }

  /**
   * Setup event listeners for collection items
   */
  #setupCollectionItems() {
    const collectionItems = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-collection-item]'));

    collectionItems.forEach((item) => {
      const button = item.querySelector('.mega-menu__collection-button');
      if (button) {
        button.addEventListener('click', (e) => this.#handleCollectionClick(e, item));
      }
    });
  }

  /**
   * Activate the first collection by default
   */
  #activateFirstCollection() {
    const firstCollection = /** @type {HTMLElement | null} */ (this.querySelector('[data-collection-item]'));
    if (firstCollection) {
      const handle = firstCollection.dataset.collectionHandle ?? null;
      this.#activeCollection = handle;
    }
  }

  /**
   * Handle collection item click
   * @param {Event} event - The click event
   * @param {HTMLElement} collectionItem - The collection item element
   */
  #handleCollectionClick(event, collectionItem) {
    event.preventDefault();
    const handle = collectionItem.dataset.collectionHandle ?? '';
    if (!handle) return;
    console.log('[MegaMenu] Collection button clicked:', handle);

    // Clear existing debounce
    if (this.#debounceTimeout) {
      clearTimeout(this.#debounceTimeout);
    }

    // Check if this item is already active in the DOM (not relying on cached state)
    const isAlreadyActive = collectionItem.classList.contains('active');
    console.log('[MegaMenu] isAlreadyActive (DOM check):', isAlreadyActive);

    if (isAlreadyActive) {
      console.log('[MegaMenu] Same collection (DOM), skipping');
      return;
    }

    // Debounce to prevent rapid clicking
    this.#debounceTimeout = setTimeout(() => {
      console.log('[MegaMenu] Processing click for handle:', handle);

      // Update active state
      const allItems = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('[data-collection-item]'));
      allItems.forEach((item) => item.classList.remove('active'));
      collectionItem.classList.add('active');

      // Update active collection
      this.#activeCollection = handle;

      // Update product display
      this.#updateProductDisplay(handle);
    }, this.#DEBOUNCE_DELAY);
  }

  /**
   * Update the product display for the given collection handle
   * @param {string} handle - The collection handle
   */
  #updateProductDisplay(handle) {
    const allProductContainers = /** @type {NodeListOf<HTMLElement>} */ (this.querySelectorAll('.collection-products'));

    allProductContainers.forEach((container) => {
      if (container.dataset.collectionHandle === handle) {
        // Show this container
        container.style.display = 'block';
        container.dataset.active = 'true';

        // Trigger fade-in animation
        container.classList.remove('fade-in');
        // Force reflow
        void container.offsetWidth;
        container.classList.add('fade-in');
      } else {
        // Hide other containers
        container.style.display = 'none';
        container.dataset.active = 'false';
        container.classList.remove('fade-in');
      }
    });

    // Update the submenu height after content change
    this.#updateSubmenuHeight();
  }

  /**
   * Update the --submenu-height CSS variable
   */
  #updateSubmenuHeight() {
    // Small delay to ensure browser has recalculated layout
    setTimeout(() => {
      const submenu = /** @type {HTMLElement | null} */ (this.closest('[ref="submenu[]"]') || this.closest('.menu-list__submenu'));
      if (!submenu) return;

      const newHeight = submenu.offsetHeight;

      // Set on ALL possible elements that might use this variable
      const headerComponent = /** @type {HTMLElement | null} */ (this.closest('header-component'));
      const headerMenu = /** @type {HTMLElement | null} */ (this.closest('header-menu-component'));
      const overflowMenu = /** @type {HTMLElement | null} */ (document.querySelector('.overflow-menu'));

      if (headerComponent) {
        headerComponent.style.setProperty('--submenu-height', `${newHeight}px`);
      }
      if (headerMenu) {
        headerMenu.style.setProperty('--submenu-height', `${newHeight}px`);
      }
      if (overflowMenu) {
        overflowMenu.style.setProperty('--submenu-height', `${newHeight}px`);
      }
    }, 50);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Clear debounce timeout
    if (this.#debounceTimeout) {
      clearTimeout(this.#debounceTimeout);
    }
  }
}

if (!customElements.get('interactive-mega-menu')) {
  customElements.define('interactive-mega-menu', InteractiveMegaMenu);
}
